import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, doc, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { Table, Tag, Button, Modal, Descriptions, Space, Input, Select, message } from 'antd';
import { EyeOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';

const { Search } = Input;
const { Option } = Select;

export default function LawyerApplications() {
  const [applications, setApplications] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => { fetchApplications(); }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'lawyerApplications'));
      const snap = await getDocs(q);
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setApplications(data);
      setFiltered(data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const handleSearch = (val) => {
    const q = val.toLowerCase();
    setFiltered(applications.filter(a =>
      a.fullName?.toLowerCase().includes(q) ||
      a.email?.toLowerCase().includes(q) ||
      a.phone?.toLowerCase().includes(q) ||
      a.barCouncilId?.toLowerCase().includes(q)
    ));
  };

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    if (val === 'all') setFiltered(applications);
    else setFiltered(applications.filter(a => (a.status || 'pending') === val));
  };

  const handleApprove = async (record) => {
    try {
      // Normalize phone — strip spaces/dashes, ensure +91 prefix
      let phone = (record.phone || '').replace(/[\s\-\(\)]/g, '');
      if (!phone.startsWith('+')) phone = '+91' + phone.replace(/^0/, '');

      // Create lawyer doc in Firestore using phone as identifier
      // The UID will be set when lawyer first logs in via OTP
      // We use phone as the doc ID for now, app will migrate to UID on first login
      const lawyerDocId = phone.replace(/\+/g, '').replace(/\s/g, '');
      
      await setDoc(doc(db, 'pendingLawyers', lawyerDocId), {
        phone,
        displayName: record.fullName || '',
        email: record.email || '',
        barCouncilId: record.barCouncilId || '',
        state: record.state || '',
        experience: record.experience || '',
        specialization: record.specialization || '',
        services: record.services || [],
        applicationId: record.id,
        applicationStatus: 'approved',
        verificationStatus: 'approved',
        createdAt: new Date(),
        chatRatePerMin: 200,   // ₹2/min default
        voiceRatePerMin: 300,  // ₹3/min default
        availabilityStatus: 'offline',
        avgRating: 0,
        totalReviews: 0,
        earnings: 0,
        practiceAreas: record.specialization ? [record.specialization] : [],
        languages: ['English', 'Hindi'],
        bio: '',
        role: 'lawyer',
      });

      // Update application status
      await updateDoc(doc(db, 'lawyerApplications', record.id), {
        status: 'approved',
        reviewedAt: new Date(),
        normalizedPhone: phone,
      });

      message.success(`Approved! Lawyer account created for ${phone}`);
      fetchApplications();
      setModalOpen(false);
    } catch (e) {
      console.error(e);
      message.error('Failed to approve: ' + e.message);
    }
  };

  const handleReject = async (id) => {
    try {
      await updateDoc(doc(db, 'lawyerApplications', id), {
        status: 'rejected',
        reviewedAt: new Date(),
      });
      message.success('Application rejected');
      fetchApplications();
      setModalOpen(false);
    } catch (e) { message.error('Failed to reject'); }
  };

  const statusColor = (s) => s === 'approved' ? 'green' : s === 'rejected' ? 'red' : 'orange';

  const columns = [
    { title: 'Name', dataIndex: 'fullName', key: 'fullName', render: t => t || '—' },
    { title: 'Phone', dataIndex: 'phone', key: 'phone', render: t => t || '—' },
    { title: 'Email', dataIndex: 'email', key: 'email', render: t => t || '—' },
    { title: 'Bar Council ID', dataIndex: 'barCouncilId', key: 'barCouncilId', render: t => t || '—' },
    { title: 'State', dataIndex: 'state', key: 'state', render: t => t || '—' },
    {
      title: 'Status', dataIndex: 'status', key: 'status',
      render: s => <Tag color={statusColor(s || 'pending')}>{(s || 'pending').toUpperCase()}</Tag>
    },
    {
      title: 'Submitted', dataIndex: 'submittedAt', key: 'submittedAt',
      render: t => t?.toDate ? t.toDate().toLocaleDateString('en-IN') : '—'
    },
    {
      title: 'Actions', key: 'actions',
      render: (_, record) => (
        <Space>
          <Button icon={<EyeOutlined />} size="small"
            onClick={() => { setSelected(record); setModalOpen(true); }}>View</Button>
          {(record.status || 'pending') === 'pending' && <>
            <Button icon={<CheckOutlined />} size="small" type="primary"
              onClick={() => handleApprove(record)}>Approve</Button>
            <Button icon={<CloseOutlined />} size="small" danger
              onClick={() => handleReject(record.id)}>Reject</Button>
          </>}
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontFamily: "'Fraunces', Georgia, serif", fontWeight: 600 }}>Therapist Applications</h2>
          <p style={{ color: '#888', margin: '4px 0 0' }}>
            {applications.filter(a => (a.status || 'pending') === 'pending').length} pending review
          </p>
        </div>
        <Space>
          <Select value={statusFilter} onChange={handleStatusFilter} style={{ width: 140 }}>
            <Option value="all">All</Option>
            <Option value="pending">Pending</Option>
            <Option value="approved">Approved</Option>
            <Option value="rejected">Rejected</Option>
          </Select>
          <Search placeholder="Search name, email, phone..." onSearch={handleSearch}
            onChange={e => handleSearch(e.target.value)} style={{ width: 260 }} allowClear />
          <Button onClick={fetchApplications}>Refresh</Button>
        </Space>
      </div>

      <Table columns={columns} dataSource={filtered} rowKey="id"
        loading={loading} pagination={{ pageSize: 20 }} size="middle" />

      <Modal
        title="Application Details"
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        width={700}
        footer={selected && (selected.status || 'pending') === 'pending' ? [
          <Button key="reject" danger onClick={() => handleReject(selected.id)}>Reject</Button>,
          <Button key="approve" type="primary" onClick={() => handleApprove(selected)}>Approve</Button>,
        ] : null}
      >
        {selected && (
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="Full Name" span={2}>{selected.fullName || '—'}</Descriptions.Item>
            <Descriptions.Item label="Phone">{selected.phone || '—'}</Descriptions.Item>
            <Descriptions.Item label="Email">{selected.email || '—'}</Descriptions.Item>
            <Descriptions.Item label="Bar Council ID">{selected.barCouncilId || '—'}</Descriptions.Item>
            <Descriptions.Item label="State">{selected.state || '—'}</Descriptions.Item>
            <Descriptions.Item label="Experience">{selected.experience ? `${selected.experience} years` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Specialization">{selected.specialization || '—'}</Descriptions.Item>
            <Descriptions.Item label="Status" span={2}>
              <Tag color={statusColor(selected.status || 'pending')}>
                {(selected.status || 'pending').toUpperCase()}
              </Tag>
            </Descriptions.Item>
            {selected.message && (
              <Descriptions.Item label="Message" span={2}>{selected.message}</Descriptions.Item>
            )}
            <Descriptions.Item label="Submitted" span={2}>
              {selected.submittedAt?.toDate ? selected.submittedAt.toDate().toLocaleString('en-IN') : '—'}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
}